
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { MessageSquare, Instagram, Youtube } from "lucide-react";
import { FaTiktok, FaDiscord } from "react-icons/fa";
import { RiTwitterXLine } from "react-icons/ri";

const VictoryShowcase = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.2]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);

  const handleSocialClick = (platform: string) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'social_link_click', {
        event_category: 'engagement',
        event_label: `Victory Section Social Click - ${platform}`,
        platform: platform,
        location: 'victory_section'
      });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, {
      threshold: 0.1
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  return <section ref={containerRef} className="relative min-h-screen overflow-visible">
      <div className="container mx-auto px-4">
        <div className={`${!isMobile ? "sticky top-0 h-screen" : "min-h-screen pt-24"} w-full flex items-center`}>
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div className={`text-white space-y-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                <h2 className="text-4xl md:text-5xl font-black uppercase">
                  Stay in the Saddle 
                </h2>
                
                <p className="text-lg">To be the top <span className="line-through">dog</span> horse in the paddock, you’ve got to stay ahead of the game! Keep up with the latest races, neigh-bourly tips, and tricks of the trade by following us on our social channels. Don’t rein yourself in - join the conversation and trot on over to connect with fellow champions.</p>

                <div className="flex items-center gap-4">
                  <a 
                    href="https://twitter.com/FrontRunnerRM" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => handleSocialClick('Twitter')}
                    className="group relative flex items-center p-3 rounded-lg bg-white/5 hover:bg-transparent transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-[#f94933]/10 overflow-hidden" 
                    aria-label="Follow us on X (Twitter)"
                  >
                    <div className="absolute inset-0 bg-[#f94933] transform origin-bottom scale-y-0 transition-transform duration-300 ease-out group-hover:scale-y-100"></div>
                    <RiTwitterXLine className="w-6 h-6 relative z-10" />
                  </a>
                  
                  <a 
                    href="https://www.tiktok.com/@frontrunnergame" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => handleSocialClick('TikTok')}
                    className="group relative flex items-center p-3 rounded-lg bg-white/5 hover:bg-transparent transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-[#f94933]/10 overflow-hidden" 
                    aria-label="Follow us on TikTok"
                  >
                    <div className="absolute inset-0 bg-[#f94933] transform origin-bottom scale-y-0 transition-transform duration-300 ease-out group-hover:scale-y-100"></div>
                    <FaTiktok className="w-6 h-6 relative z-10" />
                  </a>
                  
                  <a 
                    href="https://www.instagram.com/frontrunnergame/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => handleSocialClick('Instagram')}
                    className="group relative flex items-center p-3 rounded-lg bg-white/5 hover:bg-transparent transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-[#f94933]/10 overflow-hidden" 
                    aria-label="Follow us on Instagram"
                  >
                    <div className="absolute inset-0 bg-[#f94933] transform origin-bottom scale-y-0 transition-transform duration-300 ease-out group-hover:scale-y-100"></div>
                    <Instagram className="w-6 h-6 relative z-10" />
                  </a>
                  
                  <a 
                    href="https://www.youtube.com/@FrontRunnerGame" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => handleSocialClick('YouTube')}
                    className="group relative flex items-center p-3 rounded-lg bg-white/5 hover:bg-transparent transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-[#f94933]/10 overflow-hidden" 
                    aria-label="Subscribe to our YouTube channel"
                  >
                    <div className="absolute inset-0 bg-[#f94933] transform origin-bottom scale-y-0 transition-transform duration-300 ease-out group-hover:scale-y-100"></div>
                    <Youtube className="w-6 h-6 relative z-10" />
                  </a>
                  
                  <a 
                    href="https://discord.com/invite/meccRWwMXw" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => handleSocialClick('Discord')}
                    className="group relative flex items-center p-3 rounded-lg bg-white/5 hover:bg-transparent transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-[#f94933]/10 overflow-hidden" 
                    aria-label="Join our Discord server"
                  >
                    <div className="absolute inset-0 bg-[#f94933] transform origin-bottom scale-y-0 transition-transform duration-300 ease-out group-hover:scale-y-100"></div>
                    <FaDiscord className="w-6 h-6 relative z-10" />
                  </a>
                </div>
              </motion.div>

              <div className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
                <motion.div 
                  className={`relative ${!isMobile ? "scale-150 translate-x-40 translate-y-32" : ""} max-w-[600px] mx-auto`} 
                  style={{
                    scale: !isMobile ? scale : 1,
                    y: !isMobile ? y : 0
                  }}
                >
                  <div className="relative z-10 bg-[#222] p-2 rounded-[35px] shadow-xl ring-1 ring-gray-900/10">
                    <div className="overflow-hidden bg-black rounded-[32px]">
                      <img src="/lovable-uploads/game-view-2.webp" alt="Front Runner: Racehorse Manager Gameplay" className="w-full aspect-[16/9] object-cover" />
                    </div>
                    
                    <div className="absolute right-0 top-24 h-20 w-1 bg-gray-800 rounded-r-lg" />
                  </div>
                  
                  <div className="absolute -inset-x-4 -inset-y-4 z-0 bg-gradient-to-b from-racing-accent/20 to-transparent blur-2xl opacity-50" />
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};

export default VictoryShowcase;
